import React, { useEffect, useState } from 'react';
import { Box, Container, Typography, Alert } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import MainCard from '../../components/MainCard';
import { useOnboardingState } from './hooks/useOnboardingState';
import OnboardingWizard from './OnboardingWizard';
import { useAddSchoolMutation, useRegisterUserMutation } from '../../apis/konektApi';
import { FeeType } from '../../apis/models/students';

const OnboardingPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { state, dispatch } = useOnboardingState();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [addSchool] = useAddSchoolMutation();
  const [registerUser] = useRegisterUserMutation();

  useEffect(() => {
    const stepParam = searchParams.get('step');
    if (stepParam !== null) {
      const parsed = parseInt(stepParam, 10);
      if (!isNaN(parsed) && parsed >= 0 && parsed <= 5) {
        dispatch({ type: 'SET_STEP', payload: parsed });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleComplete = async () => {
    if (!state.school) return;
    setSubmitting(true);
    setError(null);
    try {
      // 1. Create the school with its classrooms
      await addSchool({
        schoolName: state.school.schoolName,
        classrooms: state.classrooms.map((c) => ({
          classroomName: c.name,
          classFeeCategory: c.feeCategory as FeeType,
        })),
      }).unwrap();

      // 2. Register each admin account
      for (const admin of state.admins) {
        await registerUser(admin).unwrap();
      }

      dispatch({ type: 'RESET' });
      navigate('/dashboard');
    } catch (err: any) {
      setError(err?.data?.message || t('onboarding.onboarding_error'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container maxWidth="lg">
      <MainCard>
        <Box mb={3}>
          <Typography variant="h4">{t('onboarding.title')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t('onboarding.subtitle')}
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <OnboardingWizard
          state={state}
          dispatch={dispatch}
          onComplete={handleComplete}
          submitting={submitting}
        />
      </MainCard>
    </Container>
  );
};

export default OnboardingPage;
